import { TheaterType } from '../enums/TheaterType';
import {
  Country,
  EmailCheckData,
  Opportunity,
  RegisterNewUserRequest,
  RegisterNewUserResponse,
  SalesforceAccount,
  SetOpportunityRequest,
  Theaters,
  VerifyEndUserRequest,
  WrappedResponse,
} from '../interfaces';

function isRedirectedToLogin(response: Response): boolean {
  return response.redirected && response.url.includes('auth/login');
}

function redirectToLoginAsInactive() {
  window.location.replace('/auth/login?logout=inactive');
}

function validateResponse(response: Response): boolean {
  if (response.ok) {
    if (isRedirectedToLogin(response)) {
      redirectToLoginAsInactive();
      return false;
    } else {
      return true;
    }
  }

  return false;
}

async function performRequest<T>(url: string, verb: string, bodyData: unknown = null): Promise<T> {
  const response = await fetch(url, {
    method: verb,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    body: bodyData ? JSON.stringify(bodyData) : null,
  });

  const isValidResponse = validateResponse(response);

  if (isValidResponse) {
    return response.json().catch(() => response.ok);
  } else {
    return Promise.reject({ message: response.statusText, httpStatus: response.status });
  }
}

async function getJson<T>(url: string): Promise<T> {
  return performRequest<T>(url, 'GET');
}

async function post<T>(url: string, bodyData: unknown): Promise<T> {
  return performRequest<T>(url, 'POST', bodyData);
}

async function put(url: string, bodyData: unknown = null): Promise<void> {
  return performRequest<void>(url, 'PUT', bodyData);
}

export async function getCountries(): Promise<WrappedResponse<Country[]>> {
  const url = `/api/getCountriesList`;
  return getJson<WrappedResponse<Country[]>>(url);
}

export async function getTheaters(): Promise<Theaters> {
  const url = `/api/theaters`;
  return getJson<Theaters>(url);
}

export async function getOpportunities(sfdcAccountId?: string): Promise<Opportunity[]> {
  const url = `/api/salesforce/account/${sfdcAccountId}/opportunities`;
  return getJson<Opportunity[]>(url);
}

export async function searchSalesforceAccounts(
  keyword: string,
  theaterType: TheaterType
): Promise<SalesforceAccount[]> {
  const url = `/api/salesforce/accounts/search?keyword=${keyword}&theater=${theaterType}`;
  return getJson<SalesforceAccount[]>(url);
}

export async function checkEmail(email: string): Promise<WrappedResponse<EmailCheckData>> {
  const url = `/api/capture/check?email=${encodeURIComponent(email)}`;
  return getJson<WrappedResponse<EmailCheckData>>(url);
}

export async function registerNewUser(
  registerNewUserRequest: RegisterNewUserRequest
): Promise<WrappedResponse<RegisterNewUserResponse>> {
  const url = `/api/projectverifyowner/registernewuser`;

  return post<WrappedResponse<RegisterNewUserResponse>>(url, registerNewUserRequest);
}

export async function verifyDell(projectId: number) {
  const url = `/api/project/${projectId}/ownership/verifydell`;
  return put(url);
}

export async function setDeal(projectId: number, dealId: string) {
  const url = `/api/project/${projectId}/ownership/setdeal?dealId=${dealId}`;
  return put(url);
}

export async function verifyEndUser(projectId: number, verifyEndUserRequest: VerifyEndUserRequest) {
  const url = `/api/project/${projectId}/ownership/verifyenduser`;
  return put(url, verifyEndUserRequest);
}

export async function setOpportunity(
  projectId: number,
  setOpportunityRequest: SetOpportunityRequest
) {
  const url = `/api/project/${projectId}/ownership/setOpportunity`;
  return put(url, setOpportunityRequest);
}

export async function notify(projectId: number) {
  const url = `/api/project/${projectId}/ownership/notifications/notify`;

  return getJson(url);
}
